import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosClient, getBaseUrlVersioned } from "../../../services/axiosClient";
import { FetchStatus } from "../userSettings/types";
import normalizeLanguageCodes from "./normalizeLanguageCodes";
const API_USER_PERSIST = "users/persist";
const initialState = {
    selected_original_languages: [],
    search_by_my_preferred_language: false,
    isLoaded: false,
    loadStatus: FetchStatus.idle,
    isUrlSeeded: false,
    loadedForUserId: null,
    editedFields: {
        selected_original_languages: false,
        search_by_my_preferred_language: false,
    },
};
/**
 * Takes the id of the signed-in user the read is for. The request itself is
 * scoped by the auth token; the id only tags the store, so a response that
 * lands after the account changed is recognised as stale and dropped.
 */
export const getUserPersist = createAsyncThunk("userPersist/getUserPersist", async () => {
    const apiUrl = `${getBaseUrlVersioned(null)}/${API_USER_PERSIST}`;
    const response = await axiosClient.get(apiUrl);
    return response.data;
});
export const updateUserPersist = createAsyncThunk("userPersist/updateUserPersist", async (userPersist) => {
    const apiUrl = `${getBaseUrlVersioned(null)}/${API_USER_PERSIST}`;
    const payload = {};
    if (userPersist.selected_original_languages !== undefined) {
        payload.selected_original_languages = normalizeLanguageCodes(userPersist.selected_original_languages);
    }
    if (userPersist.search_by_my_preferred_language !== undefined) {
        payload.search_by_my_preferred_language = userPersist.search_by_my_preferred_language;
    }
    const response = await axiosClient.patch(apiUrl, payload);
    return response.data;
});
export const userPersistSlice = createSlice({
    name: "userPersist",
    initialState,
    reducers: {
        /**
         * Applies a selection locally without touching the server. Only interaction
         * dispatches `updateUserPersist`, so arriving via a shared link never
         * overwrites the recipient's stored value.
         */
        setSelectedOriginalLanguages: (state, action) => {
            state.selected_original_languages = normalizeLanguageCodes(action.payload);
            state.editedFields.selected_original_languages = true;
        },
        setSearchByMyPreferredLanguage: (state, action) => {
            state.search_by_my_preferred_language = action.payload;
            state.editedFields.search_by_my_preferred_language = true;
        },
        /**
         * Seeds both controls from URL parameters and marks the state as
         * URL-seeded, so a later `GET /users/persist` leaves it alone.
         *
         * Dispatched once per page load. It is deliberately not re-applied when the
         * URL changes afterwards: the store is the source of truth from that point
         * on, and re-reading the URL would let a delayed router write feed a stale
         * value back into the controls.
         */
        applyUrlLanguageFilter: (state, action) => {
            state.selected_original_languages = normalizeLanguageCodes(action.payload.selected_original_languages);
            state.search_by_my_preferred_language = action.payload.search_by_my_preferred_language;
            state.isUrlSeeded = true;
        },
        setIsUserPersistLoaded: (state, action) => {
            state.isLoaded = action.payload;
        },
        /** Dropped on sign-out or account switch, so one account's filter never carries over to the next. */
        resetUserPersist: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(getUserPersist.pending, (state, action) => {
            state.loadStatus = FetchStatus.loading;
            state.loadedForUserId = action.meta.arg;
        })
            .addCase(getUserPersist.fulfilled, (state, action) => {
            // The account changed while this read was in flight; it belongs to someone else.
            if (action.meta.arg !== state.loadedForUserId) {
                return;
            }
            // The URL wins over persisted state, and this read can land after the
            // seed, so it must not overwrite a URL-seeded selection.
            if (!state.isUrlSeeded) {
                // Do not overwrite individual fields if the user has locally interacted with them.
                if (!state.editedFields.selected_original_languages) {
                    state.selected_original_languages = normalizeLanguageCodes(action.payload?.selected_original_languages);
                }
                if (!state.editedFields.search_by_my_preferred_language) {
                    state.search_by_my_preferred_language = Boolean(action.payload?.search_by_my_preferred_language);
                }
            }
            state.isLoaded = true;
            state.loadStatus = FetchStatus.succeeded;
        })
            .addCase(getUserPersist.rejected, (state, action) => {
            if (action.meta.arg !== state.loadedForUserId) {
                return;
            }
            // Defaults are already in state; a failed read must not block the feed.
            state.isLoaded = true;
            state.loadStatus = FetchStatus.failed;
        });
        // `updateUserPersist` deliberately has no reducer case. Local state is
        // already the source of truth and was updated optimistically, so applying
        // the response would let a slow write clobber a newer selection.
    },
});
export const { applyUrlLanguageFilter, resetUserPersist, setIsUserPersistLoaded, setSearchByMyPreferredLanguage, setSelectedOriginalLanguages } = userPersistSlice.actions;
export default userPersistSlice.reducer;
